import { useState, useEffect } from 'react';
import DataTable from '../DataTable/DataTable';
import SmartUnitSelector, { SIZE_UNITS } from '../../SmartUnitSelector/SmartUnitSelector';

const API = process.env.REACT_APP_API_URL;

const KM = {
  orange: '#F15A24', orangeLight: '#FEF0EB',
  blue: '#1A3A6B', green: '#39B54A',
  teal: '#00B4D8', border: '#E5E7EB',
  text: '#1A1A2E', muted: '#6B7280', bg: '#F9FAFB',
};

function stockColor(qty) {
  if (qty > 200) return KM.green;
  if (qty > 50) return '#F59E0B';
  return '#EF4444';
}

const EMPTY_FORM = { productId: '', variantName: '', unit: '', mrp: '', salesPrice: '', stock: '', sku: '', status: 'Active' };

const formCard = { background: '#fff', border: `1px solid ${KM.border}`, borderRadius: 12, overflow: 'hidden', marginBottom: 20 };
const formHeader = { background: KM.blue, padding: '16px 24px', display: 'flex', alignItems: 'center', gap: 10 };
const headerIcon = { width: 32, height: 32, background: 'rgba(255,255,255,0.15)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 15, fontWeight: 600 };
const fieldStyle = { display: 'flex', flexDirection: 'column', gap: 5 };
const labelStyle = { fontSize: 11, fontWeight: 500, color: KM.muted, textTransform: 'uppercase', letterSpacing: '0.05em' };
const inputStyle = { padding: '9px 12px', border: `1px solid ${KM.border}`, borderRadius: 8, fontSize: 13, color: KM.text, background: '#fff', fontFamily: 'inherit', outline: 'none', width: '100%' };
const submitBtn = { gridColumn: 'span 2', padding: 11, background: KM.orange, color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', marginTop: 4 };

export default function Variants({ showToast }) {
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [filterProduct, setFilterProduct] = useState('');

  useEffect(() => { fetchVariants(); fetchProducts(); }, []);

  const fetchVariants = async () => {
    try {
      const r = await fetch(`${API}/variants`);
      const data = await r.json();
      const flat = Array.isArray(data) ? data.map(v => ({
        ...v,
        productName: v.Product?.productName || '', // ✅ flat — now searchable
      })) : [];
      setRows(flat);
      setLoading(false);
    } catch { showToast('Load Failed', 'Could not load variants.'); setLoading(false); }
  };

  const fetchProducts = async () => {
    try {
      const r = await fetch(`${API}/products`);
      const data = await r.json();
      setProducts(Array.isArray(data) ? data : []);
    } catch { showToast('Load Failed', 'Could not load products.'); }
  };

  const openAdd = () => { setEditingId(null); setFormData(EMPTY_FORM); setShowForm(true); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const openEdit = (v) => {
    setEditingId(v.id);
    setFormData({ productId: v.productId, variantName: v.variantName, unit: v.unit || '', mrp: v.mrp, salesPrice: v.salesPrice, stock: v.stock, sku: v.sku || '', status: v.status });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isSize = SIZE_UNITS.includes(formData.unit);
    const payload = {
      ...formData,
      variantName: isSize ? formData.unit : formData.variantName,
    };
    const method = editingId ? 'PUT' : 'POST';
    const url = editingId ? `${API}/variants/update/${editingId}` : `${API}/variants/add`;
    try {
      const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (res.ok) {
        showToast(editingId ? 'Updated!' : 'Added!', editingId ? 'Variant updated successfully.' : 'New variant added.');
        setShowForm(false); setEditingId(null); setFormData(EMPTY_FORM);
        fetchVariants();
      } else {
        const err = await res.json();
        showToast('Failed', err.error || 'Operation could not be completed.');
      }
    } catch { showToast('Server Error', 'Something went wrong. Try again.'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this variant?')) return;
    try {
      const res = await fetch(`${API}/variants/${id}`, { method: 'DELETE' });
      if (res.ok) { setRows(prev => prev.filter(r => r.id !== id)); showToast('Deleted!', 'Variant removed successfully.'); }
    } catch { showToast('Delete Failed', 'Could not delete variant.'); }
  };

  const set = (field) => (e) => setFormData({ ...formData, [field]: e.target.value });
  const filtered = filterProduct ? rows.filter(r => String(r.productId) === filterProduct) : rows;
  const productName = (id) => { const p = products.find(p => p.id === Number(id)); return p ? p.productName : id; };

  const isSize = SIZE_UNITS.includes(formData.unit);

  return (
    <div>
      <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <div className="section-title">Variants Management</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select className="search-input" value={filterProduct} onChange={e => setFilterProduct(e.target.value)} style={{ width: 180 }}>
            <option value="">All Products</option>
            {products.map(p => <option key={p.id} value={p.id}>{p.productName}</option>)}
          </select>
          <button className="action-btn btn-edit" style={{ padding: '7px 16px', fontSize: 13 }}
            onClick={showForm ? () => { setShowForm(false); setEditingId(null); } : openAdd}>
            {showForm ? 'Close' : '+ Add Variant'}
          </button>
        </div>
      </div>

      {showForm && (
        <div style={formCard}>
          <div style={formHeader}>
            <div style={headerIcon}>V</div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 500, color: '#fff' }}>{editingId ? 'Edit Variant' : 'Add New Variant'}</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)', marginTop: 1 }}>Enter variant specific details below</div>
            </div>
          </div>

          <div style={{ padding: '20px 24px 24px' }}>
            <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 14, gridTemplateColumns: '1fr 1fr' }}>

              <div style={{ ...fieldStyle, gridColumn: 'span 2' }}>
                <label style={labelStyle}>Product *</label>
                <select required style={inputStyle} value={formData.productId} onChange={set('productId')}>
                  <option value="">Select Product</option>
                  {products.map(p => <option key={p.id} value={p.id}>{p.productName}</option>)}
                </select>
              </div>

              <div style={fieldStyle}>
                <label style={labelStyle}>Unit *</label>
                <SmartUnitSelector
                  value={formData.unit}
                  onChange={unit => setFormData(prev => ({ ...prev, unit }))}
                />
              </div>

              {!isSize && (
                <div style={fieldStyle}>
                  <label style={labelStyle}>Variant Name *</label>
                  <input required style={inputStyle} type="text" value={formData.variantName} onChange={set('variantName')} />
                </div>
              )}

              <div style={fieldStyle}>
                <label style={labelStyle}>MRP (₹) *</label>
                <input required style={inputStyle} type="number" min="0" step="0.01" placeholder="0.00" value={formData.mrp} onChange={set('mrp')} />
              </div>

              <div style={fieldStyle}>
                <label style={labelStyle}>Sales Price (₹) *</label>
                <input required style={inputStyle} type="number" min="0" step="0.01" placeholder="0.00" value={formData.salesPrice} onChange={set('salesPrice')} />
              </div>

              <div style={fieldStyle}>
                <label style={labelStyle}>Stock Quantity *</label>
                <input required style={inputStyle} type="number" min="0" placeholder="0" value={formData.stock} onChange={set('stock')} />
              </div>

              <div style={fieldStyle}>
                <label style={labelStyle}>SKU (Internal Code)</label>
                <input style={inputStyle} type="text" placeholder="KM-SKU-001" value={formData.sku} onChange={set('sku')} />
              </div>

              <div style={{ ...fieldStyle, gridColumn: 'span 2' }}>
                <label style={labelStyle}>Status</label>
                <select style={inputStyle} value={formData.status} onChange={set('status')}>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>

              <button type="submit" style={submitBtn}>{editingId ? 'Update Variant' : 'Save Variant'}</button>
            </form>
          </div>
        </div>
      )}

      {loading ? (
        <p style={{ color: KM.muted, fontSize: 13 }}>Loading...</p>
      ) : (
        <DataTable
          columns={['ID', 'Product', 'Unit', 'Variant', 'MRP', 'Sale Price', 'Stock', 'Status', 'Actions']}
          initialRows={filtered}
          renderRow={(row) => (
            <tr key={row.id}>
              <td style={{ fontWeight: 600, color: KM.muted }}>#{row.id}</td>
              <td style={{ fontWeight: 500 }}>{row.Product ? row.Product.productName : productName(row.productId)}</td>
              <td><span style={{ fontSize: 12, fontWeight: 600, color: KM.teal }}>{row.unit || '—'}</span></td>
              <td>{SIZE_UNITS.includes(row.unit) ? row.unit : row.variantName}</td>
              <td>₹{row.mrp}</td>
              <td style={{ fontWeight: 700 }}>₹{row.salesPrice}</td>
              <td>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: stockColor(row.stock) }} />
                  {row.stock}
                </div>
              </td>
              <td><span className={`status-pill ${row.status === 'Active' ? 'pill-active' : 'pill-inactive'}`}>{row.status}</span></td>
              <td>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="action-btn btn-edit" onClick={() => openEdit(row)}>Edit</button>
                  <button className="action-btn btn-del" onClick={() => handleDelete(row.id)}>Delete</button>
                </div>
              </td>
            </tr>
          )}
        />
      )}
    </div>
  );
}